import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { getGwChallengePicks, saveGwChallengePicks } from "../api/gwChallenge";
import { fetchLeague } from "../api/leagues";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import PageLoader from "../components/PageLoader";
import PlayerPicker, { type SelectedPlayer } from "../components/PlayerPicker";
import { widePageContainer, card, secondaryBtn , pageTitle } from "../styles";

interface Props {
  user: User;
}

export default function GwPickPage({ user }: Props) {
  const { id: leagueId, gwId } = useParams<{ id: string; gwId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const delayedLoading = useDelayedLoading(loading);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [competitionId, setCompetitionId] = useState("");
  const [isInternational, setIsInternational] = useState(false);
  const [leagueName, setLeagueName] = useState("");
  const [gwName, setGwName] = useState("");
  const [currentPickId, setCurrentPickId] = useState<string | null>(null);
  const [thisGwLocked, setThisGwLocked] = useState(false);
  /** playerId → GW display name (e.g. "GW3") for players used in OTHER gameweeks */
  const [gwPickMap, setGwPickMap] = useState<Record<string, string>>({});
  /** playerId → gwId for players used in OTHER gameweeks (needed for API calls) */
  const [gwPickIdMap, setGwPickIdMap] = useState<Record<string, string>>({});
  /** Player IDs in OTHER gameweeks that are locked — cannot be removed or moved */
  const [lockedGwPlayerIds, setLockedGwPlayerIds] = useState<string[]>([]);

  useBreadcrumbs(
    leagueName
      ? [
          { label: leagueName, to: `/leagues/${leagueId}` },
          { label: "My Selections", to: `/leagues/${leagueId}/challenge` },
          { label: gwName || "Select Player" },
        ]
      : [],
  );

  useEffect(() => {
    if (!leagueId || !gwId) return;
    Promise.all([fetchLeague(user, leagueId), getGwChallengePicks(user, leagueId)])
      .then(([league, picksData]) => {
        setCompetitionId(league.competition.id);
        setIsInternational(league.competition.type === "INTERNATIONAL");
        setLeagueName(league.name);
        const row = picksData.rows.find((r) => r.gameweekId === gwId);
        if (row) {
          setGwName(row.gameweekName);
          setCurrentPickId(row.pick?.playerId ?? null);
          setThisGwLocked(row.locked || row.completed);
        }
        // Build maps for OTHER GW picks (excluding this GW)
        const nameMap: Record<string, string> = {};
        const idMap: Record<string, string> = {};
        const lockedIds: string[] = [];
        picksData.rows.forEach((r) => {
          if (r.gameweekId !== gwId && r.pick) {
            nameMap[r.pick.playerId] = r.gameweekName;
            idMap[r.pick.playerId] = r.gameweekId;
            if (r.locked || r.completed) {
              lockedIds.push(r.pick.playerId);
            }
          }
        });
        setGwPickMap(nameMap);
        setGwPickIdMap(idMap);
        setLockedGwPlayerIds(lockedIds);
      })
      .catch(() => setError("Something went wrong."))
      .finally(() => setLoading(false));
  }, [leagueId, gwId]); // eslint-disable-line

  const goBack = () => navigate(`/leagues/${leagueId}/challenge`);

  const withSaving = async (fn: () => Promise<unknown>) => {
    if (!leagueId || !gwId) return;
    setSaving(true);
    try {
      await fn();
      goBack();
    } catch {
      setError("Failed to save. Please try again.");
      setSaving(false);
    }
  };

  /** Add a player to this GW (replaces any existing pick for this GW) */
  const handleAdd = (player: SelectedPlayer) =>
    withSaving(() =>
      saveGwChallengePicks(user, leagueId!, [{ gameweekId: gwId!, playerId: player.id }]),
    );

  /** Remove the current pick from this GW */
  const handleRemoveCurrent = (playerId: string) =>
    withSaving(() =>
      saveGwChallengePicks(user, leagueId!, [{ gameweekId: gwId!, playerId: null }]),
    );

  /** Remove a player from whichever OTHER GW they're currently in */
  const handleRemoveFromOtherGw = (playerId: string) => {
    const fromGwId = gwPickIdMap[playerId];
    if (!fromGwId) return Promise.resolve();
    return withSaving(() =>
      saveGwChallengePicks(user, leagueId!, [{ gameweekId: fromGwId, playerId: null }]),
    );
  };

  /** Move a player from their current OTHER GW into this GW */
  const handleMoveToThisGw = (playerId: string) => {
    const fromGwId = gwPickIdMap[playerId];
    if (!fromGwId) return Promise.resolve();
    return withSaving(() =>
      saveGwChallengePicks(user, leagueId!, [
        { gameweekId: fromGwId, playerId: null },
        { gameweekId: gwId!, playerId },
      ]),
    );
  };

  if (delayedLoading) return <PageLoader />;
  if (loading) return null;
  if (error) return <div style={{ padding: "2rem", color: "var(--color-danger)" }}>{error}</div>;

  // Only the current GW's own pick drives "isSelected" in the modal
  const currentAsSelected: SelectedPlayer[] = currentPickId
    ? [{ id: currentPickId, name: "", club: "", position: "" }]
    : [];

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>
      {/* Header */}
      <div
        style={{
          ...card,
          marginBottom: "1.25rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--color-text-dim)",
              marginBottom: "0.25rem",
            }}
          >
            Gameweek Challenge — {gwName}
          </div>
          <h1 style={pageTitle}>
            {thisGwLocked ? "Picks Locked" : "Select Player"}
          </h1>
          <p style={{ margin: "0.3rem 0 0", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
            {thisGwLocked
              ? `${gwName} has started — picks are locked and cannot be changed.`
              : currentPickId
              ? "Choose a different player for this gameweek."
              : "Choose one player for this gameweek. Players in other gameweeks can be moved or removed."}
          </p>
        </div>
        <button onClick={goBack} style={{ ...secondaryBtn, fontSize: "0.82rem" }}>
          ← Back
        </button>
      </div>

      {/* Locked banner — read-only view of the current pick if GW is locked */}
      {thisGwLocked && currentPickId && (
        <div style={{ ...card, marginBottom: "1.25rem", display: "flex", alignItems: "center", gap: "0.75rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
          <span style={{ fontSize: "1rem" }}>🔒</span>
          <span>
            Your pick for {gwName} is locked in.
          </span>
        </div>
      )}

      {thisGwLocked && !currentPickId && (
        <div style={{ ...card, marginBottom: "1.25rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
          🔒 No player was selected for {gwName} before it locked.
        </div>
      )}

      {saving && (
        <div style={{ ...card, marginBottom: "1.25rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
          Saving…
        </div>
      )}

      {/* Only show the player picker if this GW is not locked */}
      {competitionId && !thisGwLocked && (
        <PlayerPicker
          user={user}
          competitionId={competitionId}
          selectedPlayers={currentAsSelected}
          onSelect={handleAdd}
          isInternational={isInternational}
          addLabel={gwName ? `Add to ${gwName}` : undefined}
          removeLabel={gwName ? `Remove from ${gwName}` : undefined}
          moveToLabel={gwName ? `Move to ${gwName}` : undefined}
          gwPickMap={gwPickMap}
          gwPickIdMap={gwPickIdMap}
          lockedGwPlayerIds={lockedGwPlayerIds}
          sortByGoals={true}
          onRemoveCurrent={handleRemoveCurrent}
          onRemoveFromOtherGw={handleRemoveFromOtherGw}
          onMoveToThisGw={handleMoveToThisGw}
        />
      )}
    </div>
  );
}
